'use client';

// Steer, then queue for one chat (CONTRACT v1). The provider builds the actions
// for its thread, retires messages the answer read (`data-steer`), restores the
// ones it never read (`data-steerUnread`), and when an answer ends either sends
// what is left as the next message or holds it for the person.
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useThreadRuntime, type AttachmentAdapter } from '@assistant-ui/react';
import { authHeaders } from '@/lib/shell';
import { ResumeCancellationContext } from '@/lib/resume-cancellation';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { cancelBeforeSend } from '@/voice/cancel-before-send';
import { liveAnswer } from '@/voice/live-answer';
import {
  pendingActionScope,
  pendingActionStore,
} from '@/components/assistant-ui/pending-action-store';
import { isActionPointer } from '@/components/assistant-ui/action-confirmation';
import { createSteerActions, type SteerActions } from './actions';
import { steerHoldFor, type SteerMessage } from './parts';
import { reconcileSteerHistory } from './history';
import { steerStore } from './store';
import { confirmedStreamId } from './stream-id';
import { queuedTurnMessage } from './origin';
import { createSteerComposer } from './composer';
import { useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy from '@/i18n/ns/app-approvals';
import {
  APP_BUILD_DECISION_TOOLS,
  appBuildContinuation,
  hasAppBuildDecisions,
  subscribeAppBuildDecisions,
  takeAppBuildDecisions,
} from '@/components/assistant-ui/app-build-decisions';

export type SteerContextValue = {
  threadId: string;
  actions: SteerActions;
  submitComposer: (now?: boolean) => Promise<void>;
};

/** Exported for the browser harness, which supplies recorded actions. */
export const SteerContext = createContext<SteerContextValue | null>(null);

/** The chat's steer actions, or null where the person can't send (a reader, a harness). */
export function useSteer(): SteerContextValue | null {
  return useContext(SteerContext);
}

function stagedApprovals(message: SteerMessage | undefined): number {
  if (!message || message.role !== 'assistant') return 0;
  return message.content.filter(
    (part) =>
      part.type === 'tool-call' &&
      isActionPointer(part.result) &&
      (part.result as { status?: unknown }).status === 'confirmation_required',
  ).length;
}

export function SteerProvider({
  threadId,
  enabled,
  userId,
  companyId,
  sendBlocked = false,
  attachments,
  onError,
  children,
}: {
  threadId: string;
  enabled: boolean;
  userId: string | undefined;
  companyId: string | undefined;
  /** A send would be refused now (signed out, the company reloading, no credits): the chat's own
   * continuation waits for it to clear instead of being refused. */
  sendBlocked?: boolean;
  attachments: AttachmentAdapter;
  onError: (error: Error) => void;
  children: ReactNode;
}) {
  const thread = useThreadRuntime();
  const cancelResume = useContext(ResumeCancellationContext);
  const fetcher = useSessionBoundFetch();
  const latest = useRef({ cancelResume, fetcher });
  latest.current = { cancelResume, fetcher };

  const actions = useMemo(
    () =>
      createSteerActions({
        threadId,
        fetch: (...args) => latest.current.fetcher(...args),
        headers: authHeaders,
        liveStreamId: () =>
          confirmedStreamId(threadId, liveAnswer.current, () => thread.getState().isRunning),
        isRunning: () => thread.getState().isRunning,
        cancelRun: () => cancelBeforeSend(thread, latest.current.cancelResume),
        answerStarted: () => {
          const last = thread.getState().messages.at(-1);
          return last?.role === 'assistant' && last.content.length > 0;
        },
        append: (text, attachments, steerIds) =>
          thread.append(queuedTurnMessage(text, attachments, steerIds)),
        composerText: () => thread.composer.getState().text,
        setComposerText: (text) => thread.composer.setText(text),
        restoreAttachments: async (attachments) => {
          for (const attachment of attachments)
            if (!thread.composer.getState().attachments.some((a) => a.id === attachment.id))
              await thread.composer.addAttachment(attachment);
        },
        focusComposer: () =>
          globalThis.requestAnimationFrame?.(() =>
            globalThis.document
              ?.querySelector<HTMLTextAreaElement>('textarea.aui-composer-input')
              ?.focus({ preventScroll: true }),
          ),
      }),
    [threadId, thread],
  );

  const submitDraft = useMemo(
    () => createSteerComposer(thread.composer, attachments, actions),
    [thread, attachments, actions],
  );

  const scope = pendingActionScope(userId, companyId, threadId);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  useEffect(() => {
    if (!enabled) return;
    let wasRunning = thread.getState().isRunning;
    // The person's message that started the running answer: a Stop before any
    // output deletes it from the chat and puts it back in the composer.
    let startedBy: string | null = null;
    const lastUserId = () =>
      [...thread.getState().messages].reverse().find((message) => message.role === 'user')?.id ??
      null;
    const lastMessage = () => thread.getState().messages.at(-1) as SteerMessage | undefined;
    const holdNow = () =>
      steerHoldFor(
        lastMessage(),
        stagedApprovals(lastMessage()) +
          pendingActionStore.snapshot(scopeRef.current).filter((entry) => entry.action.canDecide)
            .length,
      );
    const sync = () => {
      const state = thread.getState();
      reconcileSteerHistory(threadId, state.messages, state.isRunning);
      if (state.isRunning && !wasRunning) {
        // A new answer started: anything still waiting for the person no longer
        // waits for a question or a card from before. It stays, marked not sent.
        steerStore.relabelHolds(threadId, 'stopped');
        startedBy = lastUserId();
      }
      if (!state.isRunning && wasRunning) {
        const retracted =
          startedBy !== null && !state.messages.some((message) => message.id === startedBy);
        if (retracted) actions.returnToComposer();
        else actions.drain(holdNow());
      }
      wasRunning = state.isRunning;
    };
    sync();
    const offThread = thread.subscribe(sync);
    // A message that could not reach the answer (it had just ended) goes next once nothing runs.
    const offStore = steerStore.subscribe(() => {
      if (thread.getState().isRunning) return;
      if (steerStore.snapshot(threadId).some((entry) => entry.state === 'next'))
        actions.drain(holdNow());
    });
    return () => {
      offThread();
      offStore();
    };
  }, [enabled, thread, threadId, actions]);

  // The App builder waits for the owner's decisions; once every one of its cards here is decided and
  // nothing else is running or queued, the chat continues with one short message (never an approval).
  const continueCopy = useNamespaceCopy(appApprovalsCopy);
  const continueCopyRef = useRef(continueCopy);
  continueCopyRef.current = continueCopy;
  useEffect(() => {
    if (!enabled || sendBlocked) return;
    const ready = () => {
      if (!hasAppBuildDecisions(threadId) || thread.getState().isRunning) return false;
      if (steerStore.snapshot(threadId).length) return false;
      return !pendingActionStore
        .snapshot(scopeRef.current)
        .some((entry) => APP_BUILD_DECISION_TOOLS.has(entry.action.tool));
    };
    // Checked once the current commit is over: a card that re-registers (removed and put back in
    // one commit) never reads as decided, and the decision's own effects have run.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tryContinue = () => {
      if (timer !== undefined || !ready()) return;
      timer = setTimeout(() => {
        timer = undefined;
        if (!ready()) return;
        // Sent as a queued turn: it can never approve an action by message.
        void thread.append(
          queuedTurnMessage(
            appBuildContinuation(takeAppBuildDecisions(threadId), continueCopyRef.current),
          ),
        );
      }, 0);
    };
    const offDecisions = subscribeAppBuildDecisions(tryContinue);
    const offPending = pendingActionStore.subscribe(tryContinue);
    const offThread = thread.subscribe(tryContinue);
    tryContinue();
    return () => {
      clearTimeout(timer);
      offDecisions();
      offPending();
      offThread();
    };
  }, [enabled, sendBlocked, thread, threadId]);

  const value = useMemo(
    () =>
      enabled
        ? { threadId, actions, submitComposer: (now?: boolean) => submitDraft(now).catch(onError) }
        : null,
    [enabled, threadId, actions, submitDraft, onError],
  );
  return <SteerContext.Provider value={value}>{children}</SteerContext.Provider>;
}
